import React, { useEffect, useState } from "react";
import { ArrowRight, Lock, Mail } from "lucide-react";
import { DotMatrixLoader } from "../icon-bank/DotMatrixLoader";
import { getRuntimeApiBase } from "../../lib/runtime-api-base";
import type { AuthFlowMode } from "../../../shared/auth-redirect";

type EmailStatus = {
  exists: boolean;
  recommendedMode: AuthFlowMode;
};

interface EmailContinuityAuthFormProps {
  apiBase?: string;
  disabled?: boolean;
  mode?: AuthFlowMode;
  onMagicLink: (email: string, mode: AuthFlowMode) => Promise<void>;
  onPassword: (input: {
    email: string;
    password: string;
    mode: AuthFlowMode;
  }) => Promise<{ needsConfirmation: boolean }>;
}

type FormStage = "email" | "credentials";
type FormStatus = "idle" | "checking" | "sending" | "sent" | "error";

const DEFAULT_API_BASE = getRuntimeApiBase();

async function fetchEmailStatus(
  email: string,
  apiBase: string,
): Promise<EmailStatus> {
  const res = await fetch(
    `${apiBase.replace(/\/$/, "")}/api/auth/email-status`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ email }),
    },
  );
  const data = (await res.json().catch(() => ({}))) as Partial<EmailStatus> & {
    error?: string;
  };
  if (!res.ok) throw new Error(data.error ?? "Email check failed.");
  return {
    exists: Boolean(data.exists),
    recommendedMode: data.recommendedMode === "login" ? "login" : "signup",
  };
}

export function EmailContinuityAuthForm({
  apiBase = DEFAULT_API_BASE,
  disabled = false,
  mode = "login",
  onMagicLink,
  onPassword,
}: EmailContinuityAuthFormProps) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [stage, setStage] = useState<FormStage>("email");
  const [flowMode, setFlowMode] = useState<AuthFlowMode>(mode);
  const [status, setStatus] = useState<FormStatus>("idle");
  const [message, setMessage] = useState("");

  useEffect(() => {
    setFlowMode(mode);
    setStage("email");
    setPassword("");
    setStatus("idle");
    setMessage("");
  }, [mode]);

  function resetForEmailChange(nextEmail: string) {
    setEmail(nextEmail);
    setPassword("");
    setStage("email");
    setFlowMode(mode);
    if (status !== "checking" && status !== "sending") {
      setStatus("idle");
      setMessage("");
    }
  }

  async function checkEmail() {
    const trimmedEmail = email.trim();
    if (!trimmedEmail) {
      setStatus("error");
      setMessage("Enter an email.");
      return;
    }

    setStatus("checking");
    setMessage("");
    try {
      const result = await fetchEmailStatus(trimmedEmail, apiBase);
      const nextMode = result.exists ? "login" : "signup";
      setFlowMode(nextMode);
      setStage("credentials");
      setStatus("idle");
      setMessage(
        result.exists
          ? "Account recognized. Continue with password or magic link."
          : "New desk. Create a password or send a magic link.",
      );
    } catch (error) {
      setStatus("error");
      setMessage(
        error instanceof Error ? error.message : "Email check failed.",
      );
    }
  }

  async function submitPassword() {
    const trimmedEmail = email.trim();
    if (!trimmedEmail || password.length < 8) {
      setStatus("error");
      setMessage("Use email and 8+ characters.");
      return;
    }

    setStatus("sending");
    setMessage("");
    try {
      const result = await onPassword({
        email: trimmedEmail,
        password,
        mode: flowMode,
      });
      setStatus("sent");
      setMessage(
        result.needsConfirmation ? "Check inbox to confirm." : "Opening desk.",
      );
    } catch (error) {
      setStatus("error");
      setMessage(error instanceof Error ? error.message : "Login failed.");
    }
  }

  async function sendMagicLink() {
    const trimmedEmail = email.trim();
    if (!trimmedEmail) {
      setStatus("error");
      setMessage("Enter an email.");
      return;
    }

    setStatus("sending");
    setMessage("");
    try {
      await onMagicLink(trimmedEmail, flowMode);
      setStatus("sent");
      setMessage(
        flowMode === "signup" ? "Check inbox to finish setup." : "Check inbox.",
      );
    } catch (error) {
      setStatus("error");
      setMessage(error instanceof Error ? error.message : "Send failed.");
    }
  }

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (stage === "email") {
      await checkEmail();
      return;
    }
    await submitPassword();
  }

  const busy = disabled || status === "checking" || status === "sending";
  const ctaLabel =
    status === "checking"
      ? "Checking"
      : status === "sending"
        ? "Sending"
        : stage === "email"
          ? "Next"
          : flowMode === "signup"
            ? "Create"
            : "Log in";

  return (
    <form className="auth-continuity-form" onSubmit={handleSubmit}>
      <style>{continuityCss}</style>
      <div
        className={`auth-continuity-lane ${
          status === "error" ? "auth-continuity-lane--error" : ""
        }`}
      >
        <div className="auth-continuity-lane__top">
          <input
            aria-label={
              mode === "signup" ? "Email for signup" : "Email for login"
            }
            autoComplete="email"
            disabled={busy}
            onChange={(event) => resetForEmailChange(event.target.value)}
            placeholder={
              mode === "signup" ? "Email for signup" : "Email for login"
            }
            type="email"
            value={email}
          />
          <button disabled={busy} type="submit">
            {status === "checking" || status === "sending" ? (
              <DotMatrixLoader variant="diagonal-scan" size={16} />
            ) : (
              <ArrowRight size={15} />
            )}
            <span>{ctaLabel}</span>
          </button>
        </div>

        {stage === "credentials" && (
          <div className="auth-continuity-lane__detail">
            <label>
              <Lock size={13} strokeWidth={1.9} />
              <input
                aria-label="Password"
                autoComplete={
                  flowMode === "signup" ? "new-password" : "current-password"
                }
                disabled={busy}
                onChange={(event) => {
                  setPassword(event.target.value);
                  if (status !== "sending") {
                    setStatus("idle");
                    setMessage("");
                  }
                }}
                placeholder={
                  flowMode === "signup" ? "Create password" : "Password"
                }
                type="password"
                value={password}
              />
            </label>
            <button
              className="auth-continuity-lane__magic"
              disabled={busy}
              onClick={sendMagicLink}
              type="button"
            >
              <Mail size={13} strokeWidth={1.9} />
              <span>Magic link</span>
            </button>
          </div>
        )}
      </div>
      {message && (
        <p
          className={`auth-continuity-message ${
            status === "error"
              ? "auth-continuity-message--error"
              : "auth-continuity-message--sent"
          }`}
        >
          {message}
        </p>
      )}
    </form>
  );
}

const continuityCss = `
  .auth-continuity-form {
    display: grid;
    width: 100%;
    gap: 0.42rem;
  }

  .auth-continuity-lane {
    display: grid;
    overflow: hidden;
    border: 1px solid color-mix(in srgb, var(--fintheon-text, #f0ead6) 7%, transparent);
    border-radius: 1.5rem;
    background: color-mix(in srgb, var(--fintheon-surface, #0a0905) 32%, transparent);
    box-shadow:
      inset 0 1px 0 color-mix(in srgb, var(--fintheon-text, #f0ead6) 5%, transparent),
      0 12px 34px rgba(0, 0, 0, 0.24);
    backdrop-filter: blur(18px) saturate(1.22);
    -webkit-backdrop-filter: blur(18px) saturate(1.22);
    transition:
      border-color 180ms cubic-bezier(0.22, 1, 0.36, 1),
      background 180ms cubic-bezier(0.22, 1, 0.36, 1);
  }

  .auth-continuity-lane:focus-within {
    border-color: color-mix(in srgb, var(--fintheon-primary, var(--fintheon-accent)) 42%, transparent);
    background: color-mix(in srgb, var(--fintheon-surface, #0a0905) 42%, transparent);
  }

  .auth-continuity-lane--error {
    border-color: color-mix(in srgb, #fca5a5 52%, transparent);
  }

  .auth-continuity-lane__top {
    display: flex;
    min-height: 54px;
  }

  .auth-continuity-lane input {
    min-width: 0;
    border: 0;
    background: transparent;
    color: color-mix(in srgb, var(--fintheon-text, #f0ead6) 88%, transparent);
    font-size: 0.88rem;
    outline: none;
  }

  .auth-continuity-lane input::placeholder {
    color: color-mix(in srgb, var(--fintheon-text, #f0ead6) 36%, transparent);
  }

  .auth-continuity-lane__top > input {
    flex: 1;
    padding: 0 1.05rem;
  }

  .auth-continuity-lane__top > button {
    display: inline-flex;
    min-width: 7.2rem;
    align-items: center;
    justify-content: center;
    gap: 0.42rem;
    border: 0;
    border-left: 1px solid color-mix(in srgb, var(--fintheon-text, #f0ead6) 7%, transparent);
    background: color-mix(in srgb, var(--fintheon-text, #f0ead6) 86%, var(--fintheon-primary, var(--fintheon-accent)) 14%);
    color: #080705;
    font-size: 0.78rem;
    font-weight: 720;
    letter-spacing: 0;
  }

  .auth-continuity-lane__detail {
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto;
    gap: 0;
    border-top: 1px solid color-mix(in srgb, var(--fintheon-text, #f0ead6) 7%, transparent);
  }

  .auth-continuity-lane__detail label,
  .auth-continuity-lane__magic {
    display: inline-flex;
    min-height: 48px;
    align-items: center;
    gap: 0.46rem;
    color: color-mix(in srgb, var(--fintheon-text, #f0ead6) 64%, transparent);
  }

  .auth-continuity-lane__detail label {
    min-width: 0;
    padding: 0 0.95rem;
  }

  .auth-continuity-lane__detail label input {
    flex: 1;
  }

  .auth-continuity-lane__magic {
    justify-content: center;
    border: 0;
    border-left: 1px solid color-mix(in srgb, var(--fintheon-text, #f0ead6) 7%, transparent);
    background: color-mix(in srgb, var(--fintheon-text, #f0ead6) 5%, transparent);
    padding: 0 0.9rem;
    font-size: 0.72rem;
    font-weight: 700;
  }

  .auth-continuity-lane button:hover:not(:disabled) {
    background: color-mix(in srgb, var(--fintheon-text, #f0ead6) 78%, var(--fintheon-primary, var(--fintheon-accent)) 22%);
  }

  .auth-continuity-lane__magic:hover:not(:disabled) {
    background: color-mix(in srgb, var(--fintheon-primary, var(--fintheon-accent)) 12%, transparent);
  }

  .auth-continuity-lane button:focus-visible {
    outline: 2px solid color-mix(in srgb, var(--fintheon-primary, var(--fintheon-accent)) 62%, transparent);
    outline-offset: -4px;
  }

  .auth-continuity-lane input:disabled,
  .auth-continuity-lane button:disabled {
    cursor: not-allowed;
    opacity: 0.56;
  }

  .auth-continuity-message {
    min-height: 1rem;
    margin: 0;
    text-align: center;
    font-size: 0.7rem;
    line-height: 1.2;
  }

  .auth-continuity-message--sent {
    color: color-mix(in srgb, var(--fintheon-primary, var(--fintheon-accent)) 78%, transparent);
  }

  .auth-continuity-message--error {
    color: color-mix(in srgb, #fca5a5 82%, transparent);
  }

  @media (max-width: 520px) {
    .auth-continuity-lane {
      border-radius: 1.05rem;
    }

    .auth-continuity-lane__top,
    .auth-continuity-lane__detail {
      grid-template-columns: 1fr;
      display: grid;
    }

    .auth-continuity-lane__top > input,
    .auth-continuity-lane__top > button,
    .auth-continuity-lane__detail label,
    .auth-continuity-lane__magic {
      min-height: 46px;
    }

    .auth-continuity-lane__top > button,
    .auth-continuity-lane__magic {
      border-left: 0;
      border-top: 1px solid color-mix(in srgb, var(--fintheon-text, #f0ead6) 7%, transparent);
    }
  }
`;
